/**
 * The key of a client address for the per-address limits of BackendCore (requests of unauthenticated HTTP clients and
 * new accounts per address, RATE_LIMIT_* / ACCOUNTS_PER_IP_*): an IPv4 address is itself (also when written IPv4-mapped,
 * ::ffff:a.b.c.d), an IPv6 address is its /64 network. A subscriber usually gets a whole /64 and can use any address in
 * it; keyed by the full /128, one client (behind the Cloudflare Pages proxy X-FWP-Client-IP carries the full address)
 * could rotate addresses for a fresh limit on every request. Logs and the idempotency keys of sign-ins keep the full
 * address. Anything that is not an IP address (e.g. 'unknown') is returned unchanged.
 *
 * Pure string logic without node:net: the core also runs in the browser (the in-process backend).
 */

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const HEXTET = /^[0-9a-f]{1,4}$/;

function ipv4Octets(text: string): number[] | null {
  const m = IPV4.exec(text);
  if (!m) return null;
  const octets = m.slice(1).map(Number);
  return octets.every((n) => n <= 255) ? octets : null;
}

/** The eight 16-bit groups of an IPv6 address (lower case; `::`, an IPv4 tail and a zone id allowed), or null. */
export function ipv6Groups(address: string): number[] | null {
  let text = address.trim().toLowerCase();
  if (text.startsWith('[') && text.endsWith(']')) text = text.slice(1, -1);
  const zone = text.indexOf('%');
  if (zone >= 0) text = text.slice(0, zone);
  const lastColon = text.lastIndexOf(':');
  if (lastColon < 0) return null;
  const tail = text.slice(lastColon + 1);
  if (tail.includes('.')) {
    const v4 = ipv4Octets(tail);
    if (!v4) return null;
    text = `${text.slice(0, lastColon + 1)}${((v4[0] << 8) | v4[1]).toString(16)}:${((v4[2] << 8) | v4[3]).toString(16)}`;
  }
  const halves = text.split('::');
  if (halves.length > 2) return null;
  const parts = (s: string) => (s === '' ? [] : s.split(':'));
  const head = parts(halves[0]);
  const rest = halves.length === 2 ? parts(halves[1]) : [];
  if (![...head, ...rest].every((g) => HEXTET.test(g))) return null;
  const missing = 8 - head.length - rest.length;
  if (halves.length === 2 ? missing < 1 : missing !== 0) return null;
  const hex = (list: string[]) => list.map((g) => parseInt(g, 16));
  return [...hex(head), ...new Array<number>(halves.length === 2 ? missing : 0).fill(0), ...hex(rest)];
}

/** See the module comment: IPv4 -> itself, IPv4-mapped IPv6 -> the IPv4 address, IPv6 -> "<first 64 bits>::/64". */
export function addressLimitKey(address: string): string {
  const text = address.trim().toLowerCase();
  if (ipv4Octets(text)) return text;
  const g = ipv6Groups(text);
  if (!g) return address;
  if (g.slice(0, 5).every((x) => x === 0) && g[5] === 0xffff) return `${g[6] >> 8}.${g[6] & 255}.${g[7] >> 8}.${g[7] & 255}`;
  return `${g
    .slice(0, 4)
    .map((x) => x.toString(16))
    .join(':')}::/64`;
}
